import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.entry';

GlobalWorkerOptions.workerSrc = pdfWorker;

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

function clampZoom(value) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(value) || MIN_ZOOM));
}

function touchDistance(touches) {
  if (!touches || touches.length < 2) return 0;
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
}

function touchMidpoint(touches) {
  if (!touches || touches.length < 2) return null;
  return {
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
  };
}

function IapPdfPage({ pdf, pageNumber, width, zoom }) {
  const canvasRef = useRef(null);
  const baseSizeRef = useRef({ width: 0, height: 0 });
  const zoomRef = useRef(zoom);
  const [error, setError] = useState('');

  useEffect(() => {
    zoomRef.current = zoom;
    const canvas = canvasRef.current;
    const base = baseSizeRef.current;
    if (!canvas || !base.width || !base.height) return;
    canvas.style.width = `${Math.ceil(base.width * zoom)}px`;
    canvas.style.height = `${Math.ceil(base.height * zoom)}px`;
  }, [zoom]);

  useEffect(() => {
    let cancelled = false;
    let renderTask = null;

    const render = async () => {
      if (!pdf || !width || width < 80) return;
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;

        // Render once at the normal fitted width. Pinch zoom changes only the
        // canvas display size, so a gesture never forces all IAP pages to
        // re-render while the member is spreading/pinching.
        const baseViewport = page.getViewport({ scale: 1 });
        const displayScale = Math.max(0.1, width / baseViewport.width);
        const pixelRatio = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
        const renderViewport = page.getViewport({ scale: displayScale * pixelRatio });
        const displayViewport = page.getViewport({ scale: displayScale });
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.width = Math.ceil(renderViewport.width);
        canvas.height = Math.ceil(renderViewport.height);
        baseSizeRef.current = {
          width: displayViewport.width,
          height: displayViewport.height,
        };
        canvas.dataset.pdfBaseWidth = String(displayViewport.width);
        canvas.dataset.pdfBaseHeight = String(displayViewport.height);

        const currentZoom = zoomRef.current;
        canvas.style.width = `${Math.ceil(displayViewport.width * currentZoom)}px`;
        canvas.style.height = `${Math.ceil(displayViewport.height * currentZoom)}px`;

        renderTask = page.render({
          canvasContext: canvas.getContext('2d', { alpha: false }),
          viewport: renderViewport,
        });
        await renderTask.promise;
        if (!cancelled) setError('');
      } catch (err) {
        if (!cancelled && err?.name !== 'RenderingCancelledException') {
          setError('Unable to render this page.');
        }
      }
    };

    render();
    return () => {
      cancelled = true;
      try { renderTask?.cancel(); } catch (_) {}
    };
  }, [pdf, pageNumber, width]);

  return (
    <div id={`iap-page-${pageNumber}`} className="iap-pdf-page" aria-label={`IAP page ${pageNumber}`}>
      {error && <div className="iap-alert">{error}</div>}
      <div className="iap-pdf-canvas-wrap">
        <canvas ref={canvasRef} />
      </div>
      <span className="iap-page-number">{pageNumber}</span>
    </div>
  );
}

export default function IapDocumentViewer({ blob, title, onError }) {
  const containerRef = useRef(null);
  const pinchRef = useRef(null);
  const zoomRef = useRef(MIN_ZOOM);
  const frameRef = useRef(null);
  const pendingZoomRef = useRef(MIN_ZOOM);
  const [pdf, setPdf] = useState(null);
  const [width, setWidth] = useState(0);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;

    const load = async () => {
      if (!blob) {
        setPdf(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const bytes = await blob.arrayBuffer();
        loadingTask = getDocument({ data: bytes });
        const document = await loadingTask.promise;
        if (cancelled) {
          document.destroy();
          return;
        }
        setPdf(document);
      } catch (err) {
        if (!cancelled) {
          const message = 'Unable to display this saved IAP.';
          setError(message);
          onError?.(message, err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      try { loadingTask?.destroy(); } catch (_) {}
    };
  }, [blob, onError]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const measure = () => setWidth(Math.max(0, Math.floor(node.getBoundingClientRect().width)));
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const applyZoom = nextZoom => {
      node.querySelectorAll('canvas[data-pdf-base-width][data-pdf-base-height]').forEach(canvas => {
        const baseWidth = Number(canvas.dataset.pdfBaseWidth || 0);
        const baseHeight = Number(canvas.dataset.pdfBaseHeight || 0);
        if (!baseWidth || !baseHeight) return;
        canvas.style.width = `${Math.ceil(baseWidth * nextZoom)}px`;
        canvas.style.height = `${Math.ceil(baseHeight * nextZoom)}px`;
      });
    };

    const pageAt = (point, eventTarget) => {
      const direct = eventTarget instanceof Element ? eventTarget.closest('.iap-pdf-page') : null;
      if (direct) return direct;
      return document.elementFromPoint(point.x, point.y)?.closest?.('.iap-pdf-page') || null;
    };

    const keepAnchor = (pinch, next, focus) => {
      const anchorCanvas = pinch?.canvas?.isConnected ? pinch.canvas : null;
      const anchorPage = pinch?.page?.isConnected ? pinch.page : null;
      if (!focus || !anchorCanvas || !anchorPage || !Number.isFinite(pinch?.anchorX) || !Number.isFinite(pinch?.anchorY)) return;

      const rect = anchorCanvas.getBoundingClientRect();
      const wantedX = rect.left + pinch.anchorX * next;
      const wantedY = rect.top + pinch.anchorY * next;
      anchorPage.scrollLeft += wantedX - focus.x;
      window.scrollBy(0, wantedY - focus.y);
    };

    const commitZoom = (nextZoom, point) => {
      pendingZoomRef.current = clampZoom(nextZoom);
      if (pinchRef.current && point) pinchRef.current.lastPoint = point;
      if (frameRef.current) return;

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        const next = pendingZoomRef.current;
        const pinch = pinchRef.current;
        const focus = pinch?.lastPoint || point || null;
        const canvas = pinch?.canvas?.isConnected ? pinch.canvas : null;

        // While fingers are moving, resize only the page being pinched. Avoiding
        // a React render and dozens of offscreen canvas layout updates every
        // frame makes long IAPs/protocols feel much closer to the native viewer.
        if (canvas) {
          const baseWidth = Number(canvas.dataset.pdfBaseWidth || 0);
          const baseHeight = Number(canvas.dataset.pdfBaseHeight || 0);
          if (baseWidth && baseHeight) {
            canvas.style.width = `${Math.ceil(baseWidth * next)}px`;
            canvas.style.height = `${Math.ceil(baseHeight * next)}px`;
          }
        }

        zoomRef.current = next;
        keepAnchor(pinch, next, focus);
      });
    };

    const onTouchStart = event => {
      if (event.touches.length !== 2) return;
      const distance = touchDistance(event.touches);
      const point = touchMidpoint(event.touches);
      if (!distance || !point) return;

      const page = pageAt(point, event.target);
      const canvas = page?.querySelector('canvas') || null;
      const rect = canvas?.getBoundingClientRect();

      pinchRef.current = {
        distance,
        zoom: zoomRef.current,
        page,
        canvas,
        anchorX: rect ? (point.x - rect.left) / zoomRef.current : null,
        anchorY: rect ? (point.y - rect.top) / zoomRef.current : null,
        lastPoint: point,
      };
      event.preventDefault();
    };

    const onTouchMove = event => {
      if (event.touches.length !== 2 || !pinchRef.current) return;
      const distance = touchDistance(event.touches);
      const point = touchMidpoint(event.touches);
      if (!distance || !point) return;

      const scale = distance / pinchRef.current.distance;
      commitZoom(pinchRef.current.zoom * scale, point);
      event.preventDefault();
    };

    const finishPinch = event => {
      if (event.touches?.length >= 2) return;
      const pinch = pinchRef.current;
      if (!pinch) return;

      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      const finalZoom = clampZoom(pendingZoomRef.current || zoomRef.current);
      zoomRef.current = finalZoom;
      const focus = pinch.lastPoint;

      // Commit the final scale to the rest of the document only once the
      // gesture ends, then correct the anchor after the surrounding pages
      // have changed size. The visual pinch itself stays lightweight.
      applyZoom(finalZoom);
      setZoom(finalZoom);
      window.requestAnimationFrame(() => keepAnchor(pinch, finalZoom, focus));
      pinchRef.current = null;
    };

    const preventNativeGesture = event => event.preventDefault();

    node.addEventListener('touchstart', onTouchStart, { passive: false });
    node.addEventListener('touchmove', onTouchMove, { passive: false });
    node.addEventListener('touchend', finishPinch, { passive: true });
    node.addEventListener('touchcancel', finishPinch, { passive: true });
    node.addEventListener('gesturestart', preventNativeGesture, { passive: false });
    node.addEventListener('gesturechange', preventNativeGesture, { passive: false });
    node.addEventListener('gestureend', preventNativeGesture, { passive: false });

    return () => {
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', finishPinch);
      node.removeEventListener('touchcancel', finishPinch);
      node.removeEventListener('gesturestart', preventNativeGesture);
      node.removeEventListener('gesturechange', preventNativeGesture);
      node.removeEventListener('gestureend', preventNativeGesture);
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, []);
  useEffect(() => () => {
    try { pdf?.destroy(); } catch (_) {}
  }, [pdf]);

  const pages = useMemo(
    () => Array.from({ length: pdf?.numPages || 0 }, (_, index) => index + 1),
    [pdf]
  );

  return (
    <div className="iap-document" ref={containerRef} aria-label={title || 'Saved incident IAP'}>
      {loading && <div className="iap-empty">Opening saved IAP…</div>}
      {error && <div className="iap-alert">{error}</div>}
      {pdf && (
        <div className="iap-pdf-pages">
          {pages.map(pageNumber => (
            <IapPdfPage
              key={pageNumber}
              pdf={pdf}
              pageNumber={pageNumber}
              width={width}
              zoom={zoom}
            />
          ))}
        </div>
      )}
    </div>
  );
}
